import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  type = 'danger',
}) => {
  if (!isOpen) return null;

  const typeConfig = {
    danger: {
      icon: <AlertTriangle className="text-red-500" size={24} />,
      iconBg: 'bg-[rgba(239,68,68,0.1)]',
      confirmBg: 'bg-red-500 hover:bg-red-600',
    },
    warning: {
      icon: <AlertTriangle className="text-yellow-500" size={24} />,
      iconBg: 'bg-[rgba(234,179,8,0.1)]',
      confirmBg: 'bg-yellow-500 hover:bg-yellow-600',
    },
    info: {
      icon: <AlertTriangle className="text-blue-500" size={24} />,
      iconBg: 'bg-[rgba(59,130,246,0.1)]',
      confirmBg: 'bg-blue-500 hover:bg-blue-600',
    },
  };

  const config = typeConfig[type];

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onCancel}
      />

      {/* Dialog */}
      <div className="relative bg-[#141414] border border-[#1F1F1F] rounded-2xl shadow-2xl max-w-md w-full mx-4 animate-scale-in">
        {/* Close button */}
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-[#5F5F5F] hover:text-white transition-colors p-1 rounded hover:bg-[#1F1F1F]"
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className={`w-12 h-12 ${config.iconBg} rounded-xl flex items-center justify-center mb-4`}>
            {config.icon}
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-white mb-2">{title}</h3>

          {/* Message */}
          <p className="text-[#8F8F8F] text-[15px] leading-relaxed mb-6">{message}</p>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 h-11 px-4 bg-[#1F1F1F] text-white font-semibold rounded-lg hover:bg-[#2F2F2F] transition-all"
            >
              {cancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onCancel();
              }}
              className={`flex-1 h-11 px-4 ${config.confirmBg} text-white font-semibold rounded-lg transition-all`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Confirmation manager for programmatic usage
let confirmCallback: ((options: ConfirmOptions) => void) | null = null;

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel?: () => void;
}

export const setConfirmCallback = (callback: (options: ConfirmOptions) => void) => {
  confirmCallback = callback;
};

export const showConfirm = (options: ConfirmOptions) => {
  if (confirmCallback) {
    confirmCallback(options);
  } else {
    console.warn('Confirm dialog system not initialized');
  }
};
